import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Page } from "@/components/Page";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { api } from "@/lib/api";

export default function Account() {
  const { user, logout } = useAuth();
  const { openEnquiry } = useUI();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/enquiries/mine").then((r) => setEnquiries(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === false) return <Navigate to="/login" replace />;

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  return (
    <Page testId="account-page">
      <section className="mx-auto min-h-screen max-w-[1100px] px-6 pb-28 pt-40 lg:pt-48">
        <FadeUp>
          <Eyebrow>Member Portal</Eyebrow>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-6 font-serif text-4xl font-medium text-ivory sm:text-5xl">
            {user ? (
              <>
                Good evening, <span className="gold-text italic">{user.name.split(" ")[0]}.</span>
              </>
            ) : (
              "…"
            )}
          </h1>
        </FadeUp>
        {user && (
          <FadeUp delay={0.2}>
            <p className="mt-4 text-[13px] font-light text-ivory-muted" data-testid="account-email">
              {user.email}
              {user.role === "admin" && (
                <span className="ml-3 text-[10px] uppercase tracking-[0.3em] text-champagne">
                  Concierge Desk
                </span>
              )}
            </p>
          </FadeUp>
        )}

        <FadeUp delay={0.3} className="mt-14">
          <div className="flex flex-wrap gap-4">
            <LuxButton onClick={() => openEnquiry("Concierge")} testId="account-new-request-button">
              New Request
            </LuxButton>
            <LuxButton variant="outline" onClick={signOut} testId="account-logout-button">
              Sign Out
            </LuxButton>
          </div>
        </FadeUp>

        <FadeUp delay={0.4} className="mt-16">
          <h2 className="font-serif text-2xl font-medium text-ivory">Your enquiries</h2>
          <div className="mt-6 border-t border-[rgba(200,164,93,0.16)]" data-testid="account-enquiries-list">
            {enquiries.length === 0 && (
              <p className="py-10 text-[13px] font-light text-ivory-muted">
                No enquiries yet — your concierge team awaits your first request.
              </p>
            )}
            {enquiries.map((e) => (
              <div
                key={e.id}
                className="grid gap-2 border-b border-[rgba(200,164,93,0.12)] py-6 sm:grid-cols-[140px_1fr_120px] sm:items-baseline sm:gap-6"
                data-testid={`account-enquiry-${e.id}`}
              >
                <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-champagne">
                  {e.service}
                </span>
                <span className="text-sm font-light text-ivory-muted">{e.message}</span>
                <span className="text-[10px] uppercase tracking-[0.24em] text-ivory-muted/60">
                  {e.status}
                </span>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>
    </Page>
  );
}
